import { useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Flex,
  Icon,
  ListItem,
  Text,
  UnorderedList,
  VStack,
} from "@chakra-ui/react";
import { FaExclamationTriangle, FaWhatsapp } from "react-icons/fa";

export interface AntiFraudNoticeProps {
  brandName: string;
  primaryColor: string;
  secondaryColor: string;
  onContinue: () => void;
  contactPhone?: string;
}

const AntiFraudNotice: React.FC<AntiFraudNoticeProps> = ({
  primaryColor,
  secondaryColor,
  onContinue,
  contactPhone,
}) => {
  const [hasRead, setHasRead] = useState(false);

  return (
    <VStack spacing={6} width="100%" maxW="420px" px={4} py={6} align="stretch">
      <Flex direction="column" align="center" textAlign="center" gap={3}>
        <Box
          filter="drop-shadow(0 0 12px rgba(229, 62, 62, 0.85))"
          aria-hidden
        >
          <Icon
            as={FaExclamationTriangle}
            w={16}
            h={16}
            color="red.400"
          />
        </Box>
        <Text
          fontSize="2xl"
          fontWeight="extrabold"
          color="white"
          letterSpacing="0.06em"
          fontFamily="Lexend Deca, sans-serif"
        >
          EVITE FRAUDES
        </Text>
        <Text fontSize="sm" color="whiteAlpha.900" lineHeight="tall" px={1}>
          Antes de continuar, leia atentamente as diretrizes abaixo
        </Text>
      </Flex>

      <Box
        bg="white"
        borderRadius="xl"
        overflow="hidden"
        boxShadow="0 12px 40px rgba(0, 0, 0, 0.35)"
      >
        <Box p={5}>
          <Text fontWeight="bold" color="gray.900" fontSize="md" mb={3}>
            Siga as orientações:
          </Text>
          <UnorderedList
            spacing={2}
            pl={1}
            color="gray.700"
            fontSize="sm"
            styleType="disc"
          >
            <ListItem>Não realize depósitos antecipados</ListItem>
            <ListItem>Não cobramos taxas para liberação</ListItem>
            <ListItem>Jamais devolva valores em conta de terceiros</ListItem>
            <ListItem>Não exigimos pagamento de comissão</ListItem>
          </UnorderedList>
        </Box>
        <Flex
          justify="center"
          align="center"
          py={2.5}
          px={4}
          bg="blue.50"
          borderTopWidth="1px"
          borderColor="blue.100"
        >
          <Text
            fontWeight="extrabold"
            fontSize="sm"
            letterSpacing="0.08em"
            color={secondaryColor}
          >
            FIQUE ATENTO
          </Text>
        </Flex>
      </Box>

      {contactPhone && (
        <Flex
          align="center"
          gap={3}
          bg="whiteAlpha.200"
          borderRadius="xl"
          px={4}
          py={3}
        >
          <Icon as={FaWhatsapp} color="green.300" boxSize={8} flexShrink={0} />
          <Box>
            <Text fontSize="xs" color="whiteAlpha.900" lineHeight="short">
              Em caso de dúvidas, contate nossos canais oficiais
            </Text>
            <Text fontSize="md" fontWeight="extrabold" color="white" mt={0.5}>
              {contactPhone}
            </Text>
          </Box>
        </Flex>
      )}

      <Checkbox
        isChecked={hasRead}
        onChange={(e) => setHasRead(e.target.checked)}
        alignItems="flex-start"
        spacing={3}
        color="white"
        sx={{
          ".chakra-checkbox__control": {
            borderColor: "whiteAlpha.800",
            _checked: {
              bg: primaryColor,
              borderColor: primaryColor,
            },
          },
        }}
      >
        <Text fontSize="sm" color="white" lineHeight="short">
          Li e entendi as orientações
        </Text>
      </Checkbox>

      <Button
        width="100%"
        borderRadius="full"
        h="14"
        fontSize="md"
        mt={1}
        bg={primaryColor}
        color="white"
        isDisabled={!hasRead}
        _hover={{ filter: hasRead ? "brightness(0.92)" : undefined }}
        _disabled={{ opacity: 0.5, cursor: "not-allowed" }}
        onClick={onContinue}
      >
        Continuar com segurança
      </Button>
    </VStack>
  );
};

export default AntiFraudNotice;
